import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

// ============ ICONS ============
const Icon = ({ children, size = 18, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>{children}</svg>
);

const Mail = (props) => <Icon {...props}><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></Icon>;
const Lock = (props) => <Icon {...props}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></Icon>;
const User = (props) => <Icon {...props}><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></Icon>;
const Eye = (props) => <Icon {...props}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></Icon>;
const EyeOff = (props) => <Icon {...props}><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></Icon>;
const ArrowRight = (props) => <Icon {...props}><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></Icon>;
const AlertCircle = (props) => <Icon {...props}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></Icon>;
const CheckCircle = (props) => <Icon {...props}><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></Icon>;
const Loader = (props) => <Icon {...props}><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></Icon>;

// Google icon (custom)
const GoogleIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// ============ SPONSY LOGO ============
function SponsyLogo({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" rx="8" fill="#BFFF00"/>
      <path d="M8 10h6c3.3 0 6 2.7 6 6s-2.7 6-6 6H8V10z" stroke="#0a0a0a" strokeWidth="2.5" fill="none"/>
      <path d="M20 10l4 6-4 6" stroke="#0a0a0a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}

// ============ AUTH PAGE COMPONENT ============
export default function AuthPage() {
  const { login, register, loginWithGoogle, error, clearError } = useAuth();
  
  const [mode, setMode] = useState('login'); // 'login', 'signup', 'forgot'
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: '',
  });
  const [formErrors, setFormErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormErrors(prev => ({ ...prev, [name]: '' }));
    clearError();
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Invalid email address';
    }

    if (mode !== 'forgot') {
      if (!formData.password) {
        errors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        errors.password = 'Password must be at least 6 characters';
      }
    }

    if (mode === 'signup') {
      if (!formData.name) {
        errors.name = 'Name is required';
      }
      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setSuccess(null);
    clearError();

    try {
      if (mode === 'login') {
        const { error } = await login(formData.email, formData.password);
        if (error) throw error;
      } else if (mode === 'signup') {
        const { error } = await register(formData.email, formData.password, { name: formData.name });
        if (error) throw error;
        setSuccess('Check your email to confirm your account!');
        setMode('login');
      } else if (mode === 'forgot') {
        // TODO: Implement password reset
        setSuccess('Password reset link sent to your email!');
      }
    } catch (err) {
      // Error is handled by AuthContext
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    clearError();
    try {
      await loginWithGoogle();
    } catch (err) {
      // Error is handled by AuthContext
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setFormErrors({});
    setSuccess(null);
    clearError();
  };

  return (
    <div className="min-h-screen bg-neutral-950 flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-neutral-900 to-neutral-950 p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgZmlsbD0iIzMzMyIgY3g9IjIwIiBjeT0iMjAiIHI9IjEiLz48L2c+PC9zdmc+')] opacity-30" />
        
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-8">
            <SponsyLogo size={48} />
            <span className="text-3xl font-bold text-white tracking-tight">Sponsy</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4 leading-tight">
            Manage your sponsorships<br />
            <span className="text-lime-400">like a pro</span>
          </h1>
          <p className="text-lg text-neutral-400 max-w-md">
            The CRM built for content creators. Track deals, manage contracts, send invoices — all in one place.
          </p>
        </div>

        <div className="relative z-10 space-y-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-lime-400/10 flex items-center justify-center shrink-0">
              <CheckCircle size={20} className="text-lime-400" />
            </div>
            <div>
              <h3 className="text-white font-medium">Pipeline Management</h3>
              <p className="text-sm text-neutral-500">Track deals from pitch to payment with our Kanban board</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-lime-400/10 flex items-center justify-center shrink-0">
              <CheckCircle size={20} className="text-lime-400" />
            </div>
            <div>
              <h3 className="text-white font-medium">Contract E-Signatures</h3>
              <p className="text-sm text-neutral-500">Send contracts and collect signatures digitally</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-lime-400/10 flex items-center justify-center shrink-0">
              <CheckCircle size={20} className="text-lime-400" />
            </div>
            <div>
              <h3 className="text-white font-medium">Professional Invoicing</h3>
              <p className="text-sm text-neutral-500">Generate and track invoices with payment status</p>
            </div>
          </div>
        </div>

        <p className="relative z-10 text-sm text-neutral-600">
          © 2024 Sponsy. All rights reserved.
        </p>
      </div>

      {/* Right side - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <SponsyLogo size={40} />
            <span className="text-2xl font-bold text-white tracking-tight">Sponsy</span>
          </div>

          <div className="bg-neutral-900/50 rounded-2xl border border-neutral-800/50 p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">
                {mode === 'login' && 'Welcome back'}
                {mode === 'signup' && 'Create your account'}
                {mode === 'forgot' && 'Reset password'}
              </h2>
              <p className="text-neutral-500">
                {mode === 'login' && 'Sign in to continue to Sponsy'}
                {mode === 'signup' && 'Start managing your sponsorships'}
                {mode === 'forgot' && "We'll send you a reset link"}
              </p>
            </div>

            {/* Success Message */}
            {success && (
              <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-3">
                <CheckCircle size={20} className="text-emerald-400 shrink-0" />
                <p className="text-sm text-emerald-300">{success}</p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3">
                <AlertCircle size={20} className="text-red-400 shrink-0" />
                <p className="text-sm text-red-300">{error}</p>
              </div>
            )}

            {/* Google Sign In */}
            {mode !== 'forgot' && (
              <>
                <button
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white hover:bg-neutral-100 text-neutral-900 font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <GoogleIcon size={20} />
                  Continue with Google
                </button>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-neutral-800"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-neutral-900/50 text-neutral-500">or continue with email</span>
                  </div>
                </div>
              </>
            )}

            {/* Auth Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'signup' && (
                <div>
                  <label className="block text-sm font-medium text-neutral-400 mb-2">Name</label>
                  <div className="relative">
                    <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className={`w-full bg-neutral-800/50 border ${formErrors.name ? 'border-red-500' : 'border-neutral-700'} rounded-xl pl-12 pr-4 py-3 text-white placeholder-neutral-500 focus:outline-none focus:border-lime-400 transition-colors`}
                    />
                  </div>
                  {formErrors.name && <p className="mt-1.5 text-sm text-red-400">{formErrors.name}</p>}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-neutral-400 mb-2">Email</label>
                <div className="relative">
                  <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    className={`w-full bg-neutral-800/50 border ${formErrors.email ? 'border-red-500' : 'border-neutral-700'} rounded-xl pl-12 pr-4 py-3 text-white placeholder-neutral-500 focus:outline-none focus:border-lime-400 transition-colors`}
                  />
                </div>
                {formErrors.email && <p className="mt-1.5 text-sm text-red-400">{formErrors.email}</p>}
              </div>

              {mode !== 'forgot' && (
                <div>
                  <label className="block text-sm font-medium text-neutral-400 mb-2">Password</label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      className={`w-full bg-neutral-800/50 border ${formErrors.password ? 'border-red-500' : 'border-neutral-700'} rounded-xl pl-12 pr-12 py-3 text-white placeholder-neutral-500 focus:outline-none focus:border-lime-400 transition-colors`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-300 transition-colors"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {formErrors.password && <p className="mt-1.5 text-sm text-red-400">{formErrors.password}</p>}
                </div>
              )}

              {mode === 'signup' && (
                <div>
                  <label className="block text-sm font-medium text-neutral-400 mb-2">Confirm Password</label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="••••••••"
                      className={`w-full bg-neutral-800/50 border ${formErrors.confirmPassword ? 'border-red-500' : 'border-neutral-700'} rounded-xl pl-12 pr-4 py-3 text-white placeholder-neutral-500 focus:outline-none focus:border-lime-400 transition-colors`}
                    />
                  </div>
                  {formErrors.confirmPassword && <p className="mt-1.5 text-sm text-red-400">{formErrors.confirmPassword}</p>}
                </div>
              )}

              {mode === 'login' && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => switchMode('forgot')}
                    className="text-sm text-lime-400 hover:text-lime-300 transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-lime-400 hover:bg-lime-300 text-black font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-lime-400/30"
              >
                {loading ? (
                  <Loader size={20} className="animate-spin" />
                ) : (
                  <>
                    {mode === 'login' && 'Sign In'}
                    {mode === 'signup' && 'Create Account'}
                    {mode === 'forgot' && 'Send Reset Link'}
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>

            {/* Mode Toggle */}
            <div className="mt-6 text-center">
              {mode === 'login' && (
                <p className="text-neutral-500">
                  Don't have an account?{' '}
                  <button
                    onClick={() => switchMode('signup')}
                    className="text-lime-400 hover:text-lime-300 font-medium transition-colors"
                  >
                    Sign up
                  </button>
                </p>
              )}
              {mode === 'signup' && (
                <p className="text-neutral-500">
                  Already have an account?{' '}
                  <button
                    onClick={() => switchMode('login')}
                    className="text-lime-400 hover:text-lime-300 font-medium transition-colors"
                  >
                    Sign in
                  </button>
                </p>
              )}
              {mode === 'forgot' && (
                <button
                  onClick={() => switchMode('login')}
                  className="text-lime-400 hover:text-lime-300 font-medium transition-colors"
                >
                  Back to sign in
                </button>
              )}
            </div>
          </div>

          {/* Terms */}
          {mode === 'signup' && (
            <p className="mt-6 text-center text-sm text-neutral-600">
              By creating an account, you agree to our{' '}
              <a href="/terms" className="text-neutral-400 hover:text-white transition-colors">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" className="text-neutral-400 hover:text-white transition-colors">Privacy Policy</a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
